/**
 * Converts an axios/fetch error into a human-friendly message.
 * Handles 403 (permission denied), 404 (not found), 409 (conflict),
 * and falls back to the server's detail message or the generic action string.
 */
/**
 * File-download endpoints request `responseType: 'arraybuffer'`, so an error
 * response arrives as raw bytes rather than a parsed object and
 * `data.detail` would be undefined — the caller would show a generic
 * "Invalid data submitted." instead of the server's actual explanation (e.g.
 * "This filter matches 90,000 rows, which is more than the 5,000-row limit for
 * PDF export"). Decoding here keeps the real message.
 */
function decodeBinaryBody(
  data: unknown,
): { detail?: string; message?: string; request_id?: string } | undefined {
  if (!data) return undefined
  if (data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
    try {
      // Honour byteOffset/byteLength so a view over a larger buffer decodes correctly.
      const bytes =
        data instanceof ArrayBuffer
          ? new Uint8Array(data)
          : new Uint8Array(
              (data as ArrayBufferView).buffer,
              (data as ArrayBufferView).byteOffset,
              (data as ArrayBufferView).byteLength,
            )
      const parsed = JSON.parse(new TextDecoder().decode(bytes))
      return parsed && typeof parsed === 'object' ? parsed : undefined
    } catch {
      // Not JSON (a partially written file, HTML error page, …) — ignore.
      return undefined
    }
  }
  return data as { detail?: string; message?: string; request_id?: string }
}

export function friendlyApiError(err: unknown, fallback = 'Something went wrong.'): string {
  if (!err || typeof err !== 'object') return fallback

  // Axios error shape
  const axiosErr = err as {
    response?: { status?: number; data?: unknown }
    message?: string
  }

  const status = axiosErr.response?.status
  const body = decodeBinaryBody(axiosErr.response?.data)
  const serverDetail = body?.detail ?? body?.message

  // A 500 from this API carries a request_id that matches the server log line
  // (`unhandled_exception request_id=…`). Surfacing it turns "something went
  // wrong" into something support can actually trace.
  if (status === 500 && body?.request_id) {
    return `${serverDetail ?? 'Something went wrong.'} (reference: ${body.request_id})`
  }

  if (status === 403) {
    // Try to surface the server's specific permission message, otherwise generic
    return serverDetail ?? "You don't have permission to perform this action."
  }
  if (status === 404) return serverDetail ?? 'The requested resource was not found.'
  if (status === 409) return serverDetail ?? 'This action conflicts with existing data.'
  if (status === 422) return serverDetail ?? 'Invalid data submitted.'
  if (status === 401) return serverDetail ?? 'Your session has expired. Please log in again.'

  // Axios network/other message
  if (axiosErr.message && !axiosErr.message.includes('status code')) return axiosErr.message

  return serverDetail ?? fallback
}
